import api from 'api';
import {
  maybeGetXMLSyncExportSettings,
  removeUndefinedFields
} from 'lib/helpers';
import {
  integrationHasFeature,
  IntegrationFeature,
  integrationHasAnyFeature
} from 'lib/integrations/features';
import { getAccountNumberForNotice } from 'lib/notice/customer';
import { ColumnService } from 'lib/services/directory';
import {
  ERequestTypes,
  ESnapshot,
  EOrganization,
  exists,
  EResponseTypes,
  ENotice,
  ENoticeDraft,
  ERef,
  EUser
} from 'lib/types';
import { IntegrationCustomerInfo } from 'lib/types/integrations/request';
import { CustomerFetchMethods, CustomerValidationState } from 'types/customers';
import { logAndCaptureException } from 'utils';
import { getFirebaseContext } from 'utils/firebase';
import * as EmailValidator from 'email-validator';
import { SyncFormat } from 'lib/types/integrations/sync';
import { LaunchDarklyFlags } from 'lib/types/launchDarklyFlags';
import { getBooleanFlag } from 'utils/flags';
import { isAnonymousFlow } from './anonymousFlow';

type FetchPublisherCustomerResult =
  | {
      success: true;
      method:
        | CustomerFetchMethods.FROM_CUSTOMER_INFO
        | CustomerFetchMethods.FROM_ACCOUNT_NUMBER;
      customer: IntegrationCustomerInfo;
    }
  | {
      success: true;
      method: CustomerFetchMethods.ALREADY_VERIFIED;
    }
  | {
      success: false;
      reason:
        | CustomerValidationState.NO_PUBLISHER_CUSTOMER_FOUND_IN_COLUMN
        | CustomerValidationState.INVALID_PUBLISHER_CUSTOMER_INFO
        | CustomerValidationState.INVALID_PUBLISHER_CUSTOMER_ID
        | CustomerValidationState.DID_NOT_SEARCH;
    };

export const fetchPublisherCustomer = async ({
  accountNumber,
  verifiedAccountNumber,
  customerInfo,
  newspaperSnap
}: {
  accountNumber: string | null;
  verifiedAccountNumber: string | null;
  customerInfo:
    | ERequestTypes['integrations/customers/:newspaperId/find']
    | null;
  newspaperSnap: ESnapshot<EOrganization> | null;
}): Promise<FetchPublisherCustomerResult> => {
  if (!exists(newspaperSnap) || (!accountNumber && !customerInfo?.email)) {
    return {
      success: false,
      reason: CustomerValidationState.NO_PUBLISHER_CUSTOMER_FOUND_IN_COLUMN
    };
  }

  const currentAccountNumberIsVerified =
    !!accountNumber && accountNumber === verifiedAccountNumber;
  if (currentAccountNumberIsVerified) {
    return {
      success: true,
      method: CustomerFetchMethods.ALREADY_VERIFIED
    };
  }

  const settings = await maybeGetXMLSyncExportSettings(newspaperSnap);

  const canFindByInfo = integrationHasFeature(
    settings?.format,
    IntegrationFeature.CUSTOMERS_FIND_BY_INFO
  );
  const shouldFindCustomerWithInfo =
    canFindByInfo &&
    !accountNumber &&
    !!customerInfo?.email &&
    !!EmailValidator.validate(customerInfo.email) &&
    !!customerInfo?.firstName &&
    !!customerInfo?.lastName;

  const canFindByAccountNumber = integrationHasFeature(
    settings?.format,
    IntegrationFeature.CUSTOMERS_VALIDATE_ACCOUNT_NUMBER
  );
  const shouldFindCustomerWithAccountNumber =
    canFindByAccountNumber && !!accountNumber;

  if (shouldFindCustomerWithInfo) {
    try {
      const resp: EResponseTypes['integrations/customers/:newspaperId/find'] =
        await api.post(
          `integrations/customers/${newspaperSnap.id}/find`,
          customerInfo
        );
      if (resp.success) {
        return {
          success: true,
          method: CustomerFetchMethods.FROM_CUSTOMER_INFO,
          customer: resp.customer
        };
      }
      return {
        success: false,
        reason: CustomerValidationState.INVALID_PUBLISHER_CUSTOMER_INFO
      };
    } catch (err) {
      logAndCaptureException(
        ColumnService.WEB_PLACEMENT,
        err,
        'failed to find publisher customer by customer info',
        {
          ...removeUndefinedFields(customerInfo),
          state: `${customerInfo.state}`,
          newspaperId: newspaperSnap.id
        }
      );
      return {
        success: false,
        reason: CustomerValidationState.INVALID_PUBLISHER_CUSTOMER_INFO
      };
    }
  }

  if (shouldFindCustomerWithAccountNumber) {
    try {
      const resp: EResponseTypes['integrations/customers/:newspaperId/find'] =
        await api.get(
          `integrations/customers/${newspaperSnap.id}/find/${accountNumber}`
        );
      if (resp.success) {
        return {
          success: true,
          method: CustomerFetchMethods.FROM_ACCOUNT_NUMBER,
          customer: resp.customer
        };
      }
      return {
        success: false,
        reason: CustomerValidationState.INVALID_PUBLISHER_CUSTOMER_ID
      };
    } catch (err) {
      logAndCaptureException(
        ColumnService.WEB_PLACEMENT,
        err,
        'failed to find publisher customer by account number',
        {
          newspaperId: newspaperSnap.id,
          accountNumber
        }
      );
      return {
        success: false,
        reason: CustomerValidationState.INVALID_PUBLISHER_CUSTOMER_ID
      };
    }
  }

  return {
    success: false,
    reason: CustomerValidationState.DID_NOT_SEARCH
  };
};

export const fetchAccountNumber = async ({
  noticeSnap,
  filerRef,
  filedByRef
}: {
  noticeSnap: ESnapshot<ENotice | ENoticeDraft> | null;
  filerRef: ERef<EUser> | null;
  filedByRef: ERef<EOrganization> | null;
}): Promise<
  | {
      success: true;
      accountNumber: string;
    }
  | {
      success: false;
    }
> => {
  if (!exists(noticeSnap)) {
    return {
      success: false
    };
  }

  const filerSnap = await filerRef?.get();
  const filerSnapExists = exists(filerSnap) ? filerSnap : undefined;

  const filedBySnap = await filedByRef?.get();
  const filedBySnapExists = exists(filedBySnap) ? filedBySnap : undefined;

  const fetchResults = await getAccountNumberForNotice(
    getFirebaseContext(),
    noticeSnap,
    filerSnapExists,
    filedBySnapExists
  );

  const fetchedAccountNumber = fetchResults?.id;

  if (fetchedAccountNumber) {
    return {
      success: true,
      accountNumber: fetchedAccountNumber
    };
  }
  return {
    success: false
  };
};

/**
 * Determines whether to show account ID management features in the placement flow.
 */
export const shouldShowAccountIdFeatures = (
  syncFormat: SyncFormat | undefined
) => {
  const enableAccountIdInPlacement = getBooleanFlag(
    LaunchDarklyFlags.ENABLE_ACCOUNT_ID_IN_PLACEMENT,
    false
  );
  const newspaperSupportsAccountIdStep = integrationHasAnyFeature(syncFormat, [
    IntegrationFeature.CUSTOMERS_FIND_BY_INFO,
    IntegrationFeature.CUSTOMERS_VALIDATE_ACCOUNT_NUMBER
  ]);

  return (
    /**
     * See APP-361 for more context
     * We don't show the accountId step in the anonymous flow.
     * The accountID step will get moved to the end of placement in the future -
     * in the anon flow, the user gets signed in halfway through after entering
     * customer information. Afterwards, they would be able to enter an account
     * number in the dedicated step if required by the paper.
     */
    !isAnonymousFlow() &&
    enableAccountIdInPlacement &&
    newspaperSupportsAccountIdStep
  );
};

export const getFirstAndLastNameFromFullName = (
  fullName: string | undefined
): { firstName?: string; lastName?: string } => {
  if (!fullName) {
    return { firstName: undefined, lastName: undefined };
  }

  // we want to remove double spaces
  const trimmedFullName = fullName.replace(/\s+/g, ' ').trim();
  const splitTrimmedFullName = trimmedFullName.split(' ');

  // if the name has only one space, return a firstName and lastName
  if (splitTrimmedFullName.length === 2) {
    return {
      firstName: splitTrimmedFullName[0],
      lastName: splitTrimmedFullName[1]
    };
  }
  // if there are a different number of spaces than in most names, set the firstName to the entire name
  // and the lastName to a blank space.
  return { firstName: trimmedFullName, lastName: undefined };
};
